import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import styles from "./MyPage.module.css";
import { useUser } from "../contexts/UserContext";
import { BillingTransactionItem, NextExpiringCRDResponse } from "../types";
import { signOut } from "firebase/auth";
import { auth } from "./firebase-config";
import {
  cancelDodoSubscription,
  getBillingTransactions,
  getNextExpiringCRD,
  withdrawMember,
} from "../components/firebaseFunctions";
import { useTranslation } from "react-i18next";

const BILLING_PAGE_SIZE = 10;

type BillingHistoryDisplayItem = BillingTransactionItem & {
  displayKey: string;
};

const isRefundTransaction = (tx: BillingTransactionItem): boolean => {
  const status = (tx.status ?? "").toLowerCase();
  const eventType = (tx.eventType ?? "").toLowerCase();
  return status.includes("refund") || eventType.includes("refund");
};

const getBillingHistoryGroupKey = (tx: BillingTransactionItem): string => {
  const provider = tx.provider ?? "unknown";
  const providerOrderId = tx.providerOrderId ?? tx.providerSubscriptionId;
  return providerOrderId
    ? `${provider}:${providerOrderId}`
    : `transaction:${tx.transactionId}`;
};

const getBillingSortTime = (tx: BillingTransactionItem): string =>
  tx.createdAt ?? tx.updatedAt ?? tx.processedAt ?? tx.refundedAt ?? "";

const mergeBillingTransactionsForDisplay = (
  transactions: BillingTransactionItem[]
): BillingHistoryDisplayItem[] => {
  const groupedItems = new Map<string, BillingHistoryDisplayItem>();

  for (const tx of transactions) {
    const displayKey = getBillingHistoryGroupKey(tx);
    const existing = groupedItems.get(displayKey);
    if (!existing) {
      groupedItems.set(displayKey, { ...tx, displayKey });
      continue;
    }

    const txIsRefund = isRefundTransaction(tx);
    const existingIsRefund = isRefundTransaction(existing);
    const productCode = existing.productCode ?? tx.productCode;
    const amountTotal = existing.amountTotal ?? tx.amountTotal;
    const currency = existing.currency ?? tx.currency;
    const provider = existing.provider ?? tx.provider;
    const providerOrderId = existing.providerOrderId ?? tx.providerOrderId;
    const providerSubscriptionId =
      existing.providerSubscriptionId ?? tx.providerSubscriptionId;
    const latestTx =
      getBillingSortTime(tx).localeCompare(getBillingSortTime(existing)) >= 0
        ? tx
        : existing;

    groupedItems.set(displayKey, {
      ...latestTx,
      displayKey,
      provider,
      providerOrderId,
      providerSubscriptionId,
      productCode,
      amountTotal,
      currency,
      creditGranted: existing.creditGranted ?? tx.creditGranted,
      creditRevoked: existing.creditRevoked ?? tx.creditRevoked,
      status: txIsRefund || existingIsRefund ? "refunded" : latestTx.status,
      eventType: txIsRefund || existingIsRefund ? "refund" : latestTx.eventType,
      refundedAt: existing.refundedAt ?? tx.refundedAt,
    });
  }

  return Array.from(groupedItems.values()).sort((a, b) =>
    getBillingSortTime(b).localeCompare(getBillingSortTime(a))
  );
};

const MyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userContext = useUser();
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const isKorean = currentLang.toLowerCase().startsWith("ko");
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [withdrawInput, setWithdrawInput] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [nextExpiringCRD, setNextExpiringCRD] =
    useState<NextExpiringCRDResponse | null>(null);
  const [billingTransactions, setBillingTransactions] = useState<
    BillingTransactionItem[]
  >([]);
  const [isBillingHistoryLoading, setIsBillingHistoryLoading] = useState(false);
  const [isBillingHistoryLoadingMore, setIsBillingHistoryLoadingMore] =
    useState(false);
  const [billingHistoryError, setBillingHistoryError] = useState("");
  const [billingNextCursor, setBillingNextCursor] = useState<string | null>(null);
  const [hasMoreBillingHistory, setHasMoreBillingHistory] = useState(false);
  const userDetails = userContext?.userDetails;
  const userId = userDetails?.id ?? "Guest";
  const displayBillingTransactions =
    mergeBillingTransactionsForDisplay(billingTransactions);

  useEffect(() => {
    if (userId === "Guest") {
      setNextExpiringCRD(null);
      return;
    }

    let disposed = false;
    const fetchNextExpiringCRD = async () => {
      try {
        const responseData = await getNextExpiringCRD(userId);
        if (!disposed) {
          setNextExpiringCRD(responseData);
        }
      } catch (error) {
        console.error("Failed to load next expiring CRD:", error);
        if (!disposed) {
          setNextExpiringCRD(null);
        }
      }
    };

    fetchNextExpiringCRD();
    return () => {
      disposed = true;
    };
  }, [userId]);

  useEffect(() => {
    if (userId === "Guest") {
      setBillingTransactions([]);
      setBillingHistoryError("");
      setIsBillingHistoryLoading(false);
      setIsBillingHistoryLoadingMore(false);
      setBillingNextCursor(null);
      setHasMoreBillingHistory(false);
      return;
    }

    let disposed = false;
    const fetchBillingHistory = async () => {
      try {
        setIsBillingHistoryLoading(true);
        setBillingHistoryError("");
        const responseData = await getBillingTransactions(
          userId,
          BILLING_PAGE_SIZE,
          null
        );
        if (!disposed) {
          setBillingTransactions(responseData.transactions ?? []);
          setBillingNextCursor(responseData.nextCursor ?? null);
          setHasMoreBillingHistory(responseData.hasMore === true);
        }
      } catch (error) {
        console.error("Failed to load payment history:", error);
        if (!disposed) {
          setBillingTransactions([]);
          setBillingNextCursor(null);
          setHasMoreBillingHistory(false);
          setBillingHistoryError(
            t("my_page.payment_history_error", {
              defaultValue: isKorean
                ? "결제 내역을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요."
                : "Failed to load payment history. Please try again later.",
            })
          );
        }
      } finally {
        if (!disposed) {
          setIsBillingHistoryLoading(false);
          setIsBillingHistoryLoadingMore(false);
        }
      }
    };

    fetchBillingHistory();
    return () => {
      disposed = true;
    };
  }, [isKorean, t, userId]);

  if (userDetails === undefined) {
    return null;
  }

  const handleLogout = async () => {
    await signOut(auth);
    navigate(`/${currentLang}/`);
  };

  const handleAccountDeletion = async () => {
    const withdrawPhrase = t("my_page.withdraw_confirm_phrase", {
      defaultValue: "I confirm withdrawal",
    });
    if (withdrawInput.trim() !== withdrawPhrase || isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);
      await withdrawMember(userDetails.id);
      await handleLogout();
    } catch (error) {
      console.error(t("my_page.account_deletion_error"), error);
      alert(t("my_page.account_deletion_failed"));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (isCancellingSubscription) {
      return;
    }

    const confirmed = window.confirm(
      t("my_page.cancel_subscription_confirm", {
        defaultValue:
          "Cancel subscription at period end? You can keep using the plan until the current billing period ends.",
      })
    );
    if (!confirmed) {
      return;
    }

    try {
      setIsCancellingSubscription(true);
      const result = await cancelDodoSubscription(userDetails.id);
      alert(
        result.message ||
          t("my_page.cancel_subscription_success", {
            defaultValue:
              "Subscription cancellation has been scheduled successfully.",
          })
      );
    } catch (error: any) {
      alert(
        error?.message ||
          t("my_page.cancel_subscription_failed", {
            defaultValue: "Failed to cancel subscription. Please try again.",
          })
      );
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const openWithdrawModal = () => {
    setWithdrawInput("");
    setIsWithdrawModalOpen(true);
  };

  const closeWithdrawModal = () => {
    if (isDeleting) return;
    setIsWithdrawModalOpen(false);
    setWithdrawInput("");
  };

  const withdrawPhrase = t("my_page.withdraw_confirm_phrase", {
    defaultValue: "I confirm withdrawal",
  });
  const withdrawWarning =
    userDetails.subscriptionType !== "Free"
      ? t("my_page.account_deletion_confirm_with_subscription")
      : t("my_page.account_deletion_confirm");
  const isWithdrawPhraseMatched = withdrawInput.trim() === withdrawPhrase;

  const formatExpiringDateTime = (utcIso: string): string => {
    const utcDateTime = new Date(utcIso);
    if (!Number.isFinite(utcDateTime.getTime())) {
      return utcIso;
    }

    return new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZoneName: "short",
    }).format(utcDateTime);
  };

  const formatBillingDateTime = (value: string | null): string => {
    if (!value) {
      return "-";
    }
    const dateTime = new Date(value);
    if (!Number.isFinite(dateTime.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(dateTime);
  };

  const formatBillingAmount = (
    amount: number | null,
    currency: string | null,
    provider: string | null
  ): string => {
    if (typeof amount !== "number" || Number.isNaN(amount)) {
      return "-";
    }

    const normalizedCurrency = (currency ?? "").toUpperCase();
    const zeroDecimalCurrencies = new Set(["KRW", "JPY", "VND"]);
    const fractionDigits = zeroDecimalCurrencies.has(normalizedCurrency) ? 0 : 2;
    const normalizedProvider = (provider ?? "").toLowerCase();
    const displayAmount =
      normalizedProvider === "dodo"
        ? amount / Math.pow(10, fractionDigits)
        : amount;

    if (normalizedCurrency.length > 0) {
      try {
        return new Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency: normalizedCurrency,
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }).format(displayAmount);
      } catch (error) {
        console.error("Failed to format currency:", error);
      }
    }

    return `${displayAmount.toLocaleString(i18n.language, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })} ${normalizedCurrency}`.trim();
  };

  const formatBillingStatus = (status: string | null): string => {
    const normalizedStatus = (status ?? "unknown").toLowerCase();
    if (normalizedStatus.includes("refund")) {
      return t("my_page.payment_status.refunded", {
        defaultValue: isKorean ? "환불됨" : "Refunded",
      });
    }
    if (normalizedStatus.includes("paid") || normalizedStatus === "success") {
      return t("my_page.payment_status.paid", {
        defaultValue: isKorean ? "결제 완료" : "Paid",
      });
    }
    if (normalizedStatus.includes("fail")) {
      return t("my_page.payment_status.failed", {
        defaultValue: isKorean ? "실패" : "Failed",
      });
    }
    if (
      normalizedStatus.includes("pending") ||
      normalizedStatus.includes("processing")
    ) {
      return t("my_page.payment_status.pending", {
        defaultValue: isKorean ? "처리 중" : "Pending",
      });
    }

    return t("my_page.payment_status.unknown", {
      defaultValue: isKorean ? "확인 필요" : "Unknown",
    });
  };

  const handleCopyOrderNumber = async (orderNumber: string | null) => {
    if (!orderNumber) {
      return;
    }
    try {
      await navigator.clipboard.writeText(orderNumber);
      alert(
        t("my_page.payment_history_order_copied", {
          defaultValue: isKorean
            ? "주문번호가 클립보드에 복사되었습니다."
            : "Order number copied to clipboard.",
        })
      );
    } catch (error) {
      console.error("Failed to copy order number:", error);
      alert(
        t("my_page.payment_history_order_copy_failed", {
          defaultValue: isKorean
            ? "주문번호 복사에 실패했습니다."
            : "Failed to copy order number.",
        })
      );
    }
  };

  const handleLoadMoreBillingHistory = async () => {
    if (
      userId === "Guest" ||
      !hasMoreBillingHistory ||
      !billingNextCursor ||
      isBillingHistoryLoading ||
      isBillingHistoryLoadingMore
    ) {
      return;
    }

    try {
      setIsBillingHistoryLoadingMore(true);
      const responseData = await getBillingTransactions(
        userId,
        BILLING_PAGE_SIZE,
        billingNextCursor
      );
      setBillingTransactions((prev) => [
        ...prev,
        ...(responseData.transactions ?? []),
      ]);
      setBillingNextCursor(responseData.nextCursor ?? null);
      setHasMoreBillingHistory(responseData.hasMore === true);
    } catch (error) {
      console.error("Failed to load more payment history:", error);
      alert(
        t("my_page.payment_history_load_more_error", {
          defaultValue: isKorean
            ? "결제 내역을 더 불러오지 못했습니다. 잠시 후 다시 시도해 주세요."
            : "Failed to load more payment history. Please try again later.",
        })
      );
    } finally {
      setIsBillingHistoryLoadingMore(false);
    }
  };

  return (
    <div className={styles.myPage}>
      <h1 className={styles.title}>{t("my_page.title")}</h1>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>
          {t("my_page.profile_section", { defaultValue: "Profile" })}
        </h2>
        <div className={styles.userDetails}>
          <div className={styles.label}>{t("my_page.id")}</div>
          <div className={`${styles.value} ${styles.userIdValue}`}>
            {userDetails.id}
          </div>

          <div className={styles.label}>{t("my_page.plan")}</div>
          <div className={styles.value}>{userDetails.subscriptionType}</div>

          <div className={styles.label}>
            {t("my_page.crd", { defaultValue: "CRD" })}
          </div>
          <div className={styles.value}>{userDetails.crd.toFixed(2)}</div>

          {nextExpiringCRD && (
            <>
              <div className={styles.label}>
                {t("my_page.expiring_crd_notice_label", {
                  defaultValue: currentLang.toLowerCase().startsWith("ko")
                    ? "소멸 예정"
                    : "Expiring Soon",
                })}
              </div>
              <div className={`${styles.value} ${styles.warningValue}`}>
                {t("my_page.expiring_crd_notice_value", {
                  defaultValue: currentLang.toLowerCase().startsWith("ko")
                    ? `${nextExpiringCRD.expiringCRD.toFixed(2)} CRD가 ${formatExpiringDateTime(nextExpiringCRD.expiresAtUtc)}에 소멸 예정입니다.`
                    : `${nextExpiringCRD.expiringCRD.toFixed(2)} CRD is scheduled to expire at ${formatExpiringDateTime(nextExpiringCRD.expiresAtUtc)}.`,
                })}
              </div>
            </>
          )}
        </div>
      </section>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>
          {t("my_page.account_section", { defaultValue: "Account" })}
        </h2>
        <div className={styles.buttonGroup}>
          {userDetails.hasSubscribed && (
            <button
              onClick={handleCancelSubscription}
              className={styles.buttonDanger}
              disabled={isCancellingSubscription}
            >
              {isCancellingSubscription
                ? t("my_page.cancel_subscription_processing", {
                    defaultValue: "Cancelling...",
                  })
                : t("my_page.cancel_subscription", {
                    defaultValue: "Cancel Subscription",
                  })}
            </button>
          )}
          {/* 작업관리 이동 버튼 삭제됨 */}
          <button onClick={handleLogout} className={styles.buttonPrimary}>
            {t("my_page.logout")}
          </button>
        </div>

        <div className={styles.legalLinks}>
          <Link to={`/${currentLang}/terms-of-service`}>
            {t("my_page.terms_of_service")}
          </Link>
          <span className={styles.dot}>•</span>
          <Link to={`/${currentLang}/privacy-policy`}>
            {t("my_page.privacy_policy")}
          </Link>
          <span className={styles.dot}>•</span>
          <button
            type="button"
            onClick={openWithdrawModal}
            className={styles.inlineDangerLink}
          >
            {t("my_page.withdraw", {
              defaultValue: t("my_page.account_deletion", {
                defaultValue: "Withdraw",
              }),
            })}
          </button>
        </div>
      </section>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>
          {t("my_page.payment_history_section", {
            defaultValue: isKorean ? "결제 내역" : "Payment History",
          })}
        </h2>
        <p className={styles.paymentHistoryHint}>
          {t("my_page.payment_history_hint", {
            defaultValue: isKorean
              ? "주문번호는 결제/환불 문의 시 필요할 수 있습니다."
              : "Order number may be required when requesting payment/refund support.",
          })}
        </p>
        {isBillingHistoryLoading && (
          <p className={styles.paymentHistoryMessage}>
            {t("my_page.payment_history_loading", {
              defaultValue: isKorean
                ? "결제 내역을 불러오는 중입니다..."
                : "Loading payment history...",
            })}
          </p>
        )}
        {!isBillingHistoryLoading && billingHistoryError.length > 0 && (
          <p className={`${styles.paymentHistoryMessage} ${styles.errorText}`}>
            {billingHistoryError}
          </p>
        )}
        {!isBillingHistoryLoading &&
          billingHistoryError.length === 0 &&
          displayBillingTransactions.length === 0 && (
            <p className={styles.paymentHistoryMessage}>
              {t("my_page.payment_history_empty", {
                defaultValue: isKorean
                  ? "표시할 결제 내역이 없습니다."
                  : "No payment history available.",
              })}
            </p>
          )}
        {!isBillingHistoryLoading &&
          billingHistoryError.length === 0 &&
          displayBillingTransactions.length > 0 && (
            <>
              <div className={styles.paymentHistoryList}>
                {displayBillingTransactions.map((tx) => {
                  const orderNumber = tx.providerOrderId ?? tx.transactionId;
                  return (
                    <div key={tx.displayKey} className={styles.paymentHistoryItem}>
                      <div className={styles.paymentHistoryHeader}>
                        <span className={styles.statusChip}>
                          {formatBillingStatus(tx.status)}
                        </span>
                        <span className={styles.paymentHistoryDate}>
                          {formatBillingDateTime(tx.createdAt ?? tx.updatedAt)}
                        </span>
                      </div>
                      <div className={styles.paymentHistoryGrid}>
                        <div className={styles.label}>
                          {t("my_page.payment_history_product", {
                            defaultValue: isKorean ? "상품" : "Product",
                          })}
                        </div>
                        <div className={styles.value}>{tx.productCode ?? "-"}</div>

                        <div className={styles.label}>
                          {t("my_page.payment_history_amount", {
                            defaultValue: isKorean ? "결제 금액" : "Amount",
                          })}
                        </div>
                        <div className={styles.value}>
                          {formatBillingAmount(
                            tx.amountTotal,
                            tx.currency,
                            tx.provider
                          )}
                        </div>

                        <div className={styles.label}>
                          {t("my_page.payment_history_order_number", {
                            defaultValue: isKorean ? "주문번호" : "Order Number",
                          })}
                        </div>
                        <div className={styles.valueInline}>
                          <span className={styles.codeValue}>{orderNumber}</span>
                          {orderNumber && (
                            <button
                              type="button"
                              className={styles.copyButton}
                              onClick={() => handleCopyOrderNumber(orderNumber)}
                            >
                              {t("my_page.payment_history_copy", {
                                defaultValue: isKorean ? "복사" : "Copy",
                              })}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {hasMoreBillingHistory && (
                <div className={styles.loadMoreContainer}>
                  <button
                    type="button"
                    className={styles.loadMoreButton}
                    onClick={handleLoadMoreBillingHistory}
                    disabled={isBillingHistoryLoadingMore}
                  >
                    {isBillingHistoryLoadingMore
                      ? t("my_page.payment_history_loading_more", {
                          defaultValue: isKorean
                            ? "더 불러오는 중..."
                            : "Loading more...",
                        })
                      : t("my_page.payment_history_load_more", {
                          defaultValue: isKorean ? "더보기" : "Load More",
                        })}
                  </button>
                </div>
              )}
            </>
          )}
      </section>

      {isWithdrawModalOpen && (
        <div
          className={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="withdraw-modal-title"
        >
          <div className={styles.modalCard}>
            <h3 id="withdraw-modal-title" className={styles.modalTitle}>
              {t("my_page.withdraw_modal_title", {
                defaultValue: "Confirm account withdrawal",
              })}
            </h3>
            <p className={styles.modalDescription}>{withdrawWarning}</p>
            <p className={styles.modalHint}>
              {t("my_page.withdraw_input_label", {
                defaultValue: "Type the confirmation phrase shown below.",
              })}
            </p>
            <p className={styles.modalPhrase}>{withdrawPhrase}</p>
            <input
              type="text"
              value={withdrawInput}
              onChange={(e) => setWithdrawInput(e.target.value)}
              placeholder={withdrawPhrase}
              className={styles.modalInput}
              autoComplete="off"
              autoFocus
            />
            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={closeWithdrawModal}
                className={styles.modalCancelButton}
              >
                {t("my_page.withdraw_cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                onClick={handleAccountDeletion}
                className={styles.modalDangerButton}
                disabled={!isWithdrawPhraseMatched || isDeleting}
              >
                {isDeleting
                  ? t("my_page.withdraw_processing", {
                      defaultValue: "Processing...",
                    })
                  : t("my_page.withdraw_confirm_button", {
                      defaultValue: "Delete account",
                    })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPage;
